/**
 * Role-specific tour step definitions for the software onboarding tour.
 * Uses driver.js format: { element: selector, popover: { title, description, side, align } }
 * Descriptions support HTML for richer card content.
 */

const SIDEBAR_POPOVER = { side: 'right', align: 'start' };

export const TOUR_STEPS = {
  ADMIN: [
    {
      element: '#tour-sidebar',
      popover: {
        title: 'Welcome to PICL Vendor Management',
        description:
          '<strong>Main navigation</strong><br/>As an Admin, you have full access to all features. Let us walk you through each section.',
        ...SIDEBAR_POPOVER,
      },
    },
    {
      element: 'a[href="/dashboard-overview"]',
      popover: {
        title: 'Dashboard',
        description:
          '<strong>At a glance:</strong> KPIs, vendor distribution charts, country and MSME splits, and onboarding trends.',
        ...SIDEBAR_POPOVER,
      },
    },
    {
      element: 'a[href="/vendor-master-list"]',
      popover: {
        title: 'Vendor Master',
        description:
          '<strong>Manage vendors:</strong> Search, export to Excel, assign vendor codes, and invite vendors from here.',
        ...SIDEBAR_POPOVER,
      },
    },
    {
      element: 'a[href="/vendor-approval-workflow"]',
      popover: {
        title: 'Approvals',
        description:
          '<strong>Review submissions:</strong> Approve or reject vendor registrations. Complete questionnaires and assign vendor codes.',
        ...SIDEBAR_POPOVER,
      },
    },
    {
      element: 'a[href="/supplier-requests"]',
      popover: {
        title: 'Requests',
        description:
          '<strong>Team requests:</strong> View supplier requests submitted by your team. Approve, reject, or trigger vendor invites.',
        ...SIDEBAR_POPOVER,
      },
    },
    {
      element: 'a[href="/invite-management"]',
      popover: {
        title: 'Invite Management',
        description:
          '<strong>Send invites:</strong> Vendor registration invites. Track pending, expired, and completed invites.',
        ...SIDEBAR_POPOVER,
      },
    },
    {
      element: 'a[href="/vendor-regularization"]',
      popover: {
        title: 'Vendor Regularization',
        description:
          '<strong>Bulk re-registration:</strong> Legacy vendors. Create batches and send tokens for vendors to update their data.',
        ...SIDEBAR_POPOVER,
      },
    },
    {
      element: 'a[href="/conflicting-vendors"]',
      popover: {
        title: 'Conflicting Vendors',
        description:
          '<strong>Resolve conflicts:</strong> Vendors whose questionnaire responses conflict with their registration data.',
        ...SIDEBAR_POPOVER,
      },
    },
    {
      element: 'a[href="/user-management"]',
      popover: {
        title: 'User Management',
        description:
          '<strong>Internal users:</strong> Create, edit, and manage company users. Assign roles and reset passwords.',
        ...SIDEBAR_POPOVER,
      },
    },
    {
      element: 'a[href="/vendor-excel-import"]',
      popover: {
        title: 'Excel Import',
        description:
          '<strong>Bulk import:</strong> Vendors from an Excel file. Supports clean import to replace existing data.',
        ...SIDEBAR_POPOVER,
      },
    },
  ],

  COORDINATOR: [
    {
      element: '#tour-sidebar',
      popover: {
        title: 'Welcome to PICL Vendor Management',
        description:
          '<strong>Main navigation</strong><br/>As a Coordinator, you can manage vendors and approvals. Let us walk you through each section.',
        ...SIDEBAR_POPOVER,
      },
    },
    {
      element: 'a[href="/dashboard-overview"]',
      popover: {
        title: 'Dashboard',
        description:
          '<strong>At a glance:</strong> KPIs, vendor distribution, and onboarding trends.',
        ...SIDEBAR_POPOVER,
      },
    },
    {
      element: 'a[href="/vendor-approval-workflow"]',
      popover: {
        title: 'Approvals',
        description:
          '<strong>Review submissions:</strong> Approve or reject vendor registrations. Complete questionnaires and assign vendor codes.',
        ...SIDEBAR_POPOVER,
      },
    },
    {
      element: 'a[href="/vendor-master-list"]',
      popover: {
        title: 'Vendor Master',
        description:
          '<strong>View vendors:</strong> Search and view all approved vendors. Export to Excel and manage details.',
        ...SIDEBAR_POPOVER,
      },
    },
    {
      element: 'a[href="/supplier-requests"]',
      popover: {
        title: 'Requests',
        description:
          '<strong>Team requests:</strong> Track supplier requests submitted by your team. Approve, reject, or trigger vendor invites.',
        ...SIDEBAR_POPOVER,
      },
    },
    {
      element: 'a[href="/invite-management"]',
      popover: {
        title: 'Invite Management',
        description:
          '<strong>Send invites:</strong> Vendor registration invites. Track pending, expired, and completed invites.',
        ...SIDEBAR_POPOVER,
      },
    },
    {
      element: 'a[href="/conflicting-vendors"]',
      popover: {
        title: 'Conflicting Vendors',
        description:
          '<strong>Resolve conflicts:</strong> Vendors whose questionnaire responses conflict with their registration data.',
        ...SIDEBAR_POPOVER,
      },
    },
  ],

  USER: [
    {
      element: '#tour-sidebar',
      popover: {
        title: 'Welcome to PICL Vendor Management',
        description:
          '<strong>Your workspace</strong><br/>As a User, you can submit and track supplier requests. Let us show you around.',
        ...SIDEBAR_POPOVER,
      },
    },
    {
      element: 'a[href="/supplier-requests"]',
      popover: {
        title: 'Requests',
        description:
          '<strong>Submit & track:</strong> View your supplier requests. Use the "New Request" button on the Requests page to submit a vendor onboarding request.',
        ...SIDEBAR_POPOVER,
      },
    },
  ],
};
